import { EnvironmentTwoTone } from '@ant-design/icons';
import { Timeline, Typography } from 'antd';
import type React from 'react';
import { getStatusColor } from '@/core/utils/get_status_color';
import dayjs from '@/utils/dayjs-instance';
import { getOrderChangeStatusName, type OrderChange } from '../../utils/orderChange';

const { Text } = Typography;

interface OrderChangeTimelineProps {
  orderChanges: ReadonlyArray<OrderChange>;
  departmentTimeZone: number;
  onNavigateToCoords?: (coords: { lat: number; lng: number }, statusName: string) => void;
}

const formatChangeTime = (isoString: string, departmentTimeZone: number): string =>
  dayjs
    .utc(isoString)
    .utcOffset(departmentTimeZone * 60)
    .format('HH:mm');

export const OrderChangeTimeline: React.FC<OrderChangeTimelineProps> = ({
  orderChanges,
  departmentTimeZone,
  onNavigateToCoords,
}) => {
  if (orderChanges.length === 0) {
    return null;
  }

  return (
    <Timeline
      style={{ marginTop: 16 }}
      items={orderChanges.map((change) => {
        const statusName = getOrderChangeStatusName(change);
        const statusColor = getStatusColor(statusName);
        const geoEvent = change.geo_event;

        return {
          key: change.id,
          color: statusColor,
          content: (
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              data-testid={`timeline-item-${change.id}`}
            >
              <Text type="secondary" style={{ minWidth: 40 }}>
                {formatChangeTime(change.createdAt, departmentTimeZone)}
              </Text>
              <Text strong style={{ color: statusColor }}>
                {statusName}
              </Text>
              {geoEvent && (
                <button
                  type="button"
                  onClick={() =>
                    onNavigateToCoords?.({ lat: geoEvent.lat, lng: geoEvent.lng }, statusName)
                  }
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 2,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  aria-label={`Navigate to ${statusName} location`}
                  data-testid={`geo-nav-${change.id}`}
                >
                  <EnvironmentTwoTone />
                </button>
              )}
            </div>
          ),
        };
      })}
    />
  );
};
